/**
 * Contact Detail Page
 * 
 * Shows full history and details of a campaign contact including:
 * - Contact variables/data
 * - All phone numbers with status
 * - Call history
 * - Manual conversion marking
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  alpha,
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  ArrowBack,
  Phone,
  CheckCircle,
  Error as ErrorIcon,
  Verified,
  Block,
  Warning,
  DoNotDisturb,
  Badge,
  PhoneIphone,
  BarChart,
  Refresh,
  RestartAlt,
  HelpOutline,
} from '@mui/icons-material';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import LinkPagination from '../components/LinkPagination';
import DetailHeader from '../components/DetailHeader';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useNumberFormat } from '../hooks/useNumberFormat';
import type {
  CallHistoryItem,
  CallStatus,
  CampaignContactDetail,
  CampaignType,
} from '../types';
import { formatPhone } from '../utils/phone';
import { usePermissions } from '../contexts/PermissionContext';
import { useAuth } from '../contexts/AuthContext';
import { useCallDetailDrawer } from '../contexts/CallDetailDrawerContext';
import { useContactWebSocket, type ContactObserverEvent } from '../hooks/useContactWebSocket';
import { useContactCallRealtime } from '../hooks/useContactCallRealtime';
import { useListSearchParams } from '../hooks/useListSearchParams';
import usePageTitle from '../hooks/usePageTitle';
import CallStatusChip from '../components/CallStatusChip';
import CallConsumptionDisplay from '../components/CallConsumptionDisplay';
import ContactConsumptionDisplay from '../components/ContactConsumptionDisplay';
import { formatDateTime } from '../utils/date';

interface PreCallExecutionHistoryItem {
  executionId: string;
  status: string;
  decision: 'proceed' | 'retry' | 'skip';
  reasonCode: string;
  nextRetryAt?: string | null;
  validUntil?: string | null;
  executionTimeMs?: number | null;
  createdAt: string;
}

const phoneStatusIcons: Record<string, React.ReactNode> = {
  active: <CheckCircle color="success" fontSize="small" />,
  mismatch: <Warning color="warning" fontSize="small" />,
  invalid: <ErrorIcon color="error" fontSize="small" />,
  blocked: <Block color="error" fontSize="small" />,
};

// Format display name - if it looks like a phone number, format it
function formatDisplayName(name: string): string {
  const cleaned = name.replace(/[\s\-()]/g, '');
  if (cleaned.startsWith('+') || /^\d{8,}$/.test(cleaned)) {
    return formatPhone(name);
  }
  return name;
}

// Get translated call status label
// Component for call rows in the table
interface CallDetailRowProps {
  call: CallHistoryItem;
  canViewCostCurrency?: boolean;
  isReverseIvrCampaign?: boolean;
  isSelected?: boolean;
  onSelect?: (call: CallHistoryItem) => void;
  rowRef?: (element: HTMLTableRowElement | null) => void;
}

function CallDetailRow({
  call,
  canViewCostCurrency = false,
  isReverseIvrCampaign = false,
  isSelected = false,
  onSelect,
  rowRef
}: CallDetailRowProps) {
  const { t } = useTranslation(['components', 'pages', 'common']);
  const { formatCurrency, formatDecimal, formatAverage, locale } = useNumberFormat();
  const theme = useTheme();
  const showOriginAndAgentColumns = !isReverseIvrCampaign;
  const showConsumptionColumn = !isReverseIvrCampaign;
  const showLatencyAndCostColumns = canViewCostCurrency && !isReverseIvrCampaign;
  const handleRowClick = () => {
    onSelect?.(call);
  };
  return (
    <TableRow 
      ref={rowRef}
      hover 
      sx={{ 
        cursor: 'pointer',
        bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.08) : undefined,
        transition: 'background-color 120ms ease-in-out',
      }}
      onClick={handleRowClick}
    >
      <TableCell>
        <Typography variant="body2">{formatDateTime(call.createdAt, { locale })}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{formatPhone(call.toNumber)}</Typography>
        {call.phoneLabel && (
          <Typography variant="caption" color="text.secondary">{call.phoneLabel}</Typography>
        )}
      </TableCell>
      {showOriginAndAgentColumns && (
        <TableCell>
          <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>{call.agentName || '-'}</Typography>
        </TableCell>
      )}
      {showOriginAndAgentColumns && (
        <TableCell>
          {call.originLineName ? (
            <Tooltip title={call.fromNumber ? formatPhone(call.fromNumber) : ''}>
              <Typography variant="body2" sx={{ fontSize: '0.875rem' }} noWrap>
                {call.originLineName}
              </Typography>
            </Tooltip>
          ) : (
            '-'
          )}
        </TableCell>
      )}
      <TableCell>
        <CallStatusChip status={call.status as CallStatus} isConverted={call.isConverted} isOptout={call.isOptout} isMismatch={call.isMismatch} />
      </TableCell>
      {showConsumptionColumn && (
        <TableCell>
          <CallConsumptionDisplay
            segments={call.consumptionSegments}
            activeSegmentId={call.activeSegmentId}
            activeSegmentType={call.activeSegmentType}
            callStatus={call.status}
            fallbackBilledSeconds={call.segmentTotals?.totalBilledSeconds ?? call.billedSeconds ?? call.duration ?? 0}
            fallbackDurationSeconds={call.segmentTotals?.totalDurationSeconds ?? call.duration ?? 0}
            fallbackBucketType={call.callType === 'ai_agent' ? 'ai' : 'telephony'}
            compact
          />
        </TableCell>
      )}
      {showLatencyAndCostColumns && (
        <TableCell>
          {call.avgResponseLatency != null ? (
            <Typography
              variant="body2"
              sx={{
                color: call.avgResponseLatency > 1.5 ? 'error.main' : call.avgResponseLatency > 1.0 ? 'warning.main' : 'success.main',
                fontWeight: 500,
              }}
            >
              {formatAverage(call.avgResponseLatency, formatDecimal)}s
            </Typography>
          ) : '-'}
        </TableCell>
      )}
      {showLatencyAndCostColumns && (
        <TableCell align="right">
          <Typography variant="body2" color="text.secondary">
            {call.estimatedCostUsd ? formatCurrency(Number(call.estimatedCostUsd), 'USD') : '-'}
          </Typography>
        </TableCell>
      )}
      <TableCell>
        {call.isConverted ? (
          <Chip label={t('pages:contacts.yes')} size="small" color="success" />
        ) : (
          <Typography color="text.secondary">{t('pages:contacts.no')}</Typography>
        )}
      </TableCell>
      <TableCell>
        {call.isOptout ? (
          <Chip label={t('pages:contacts.yes')} size="small" sx={{ bgcolor: '#9c27b0', color: 'white' }} />
        ) : (
          <Typography color="text.secondary">{t('pages:contacts.no')}</Typography>
        )}
      </TableCell>
      <TableCell>
        {call.isMismatch ? (
          <Chip label={t('pages:contacts.yes')} size="small" color="warning" />
        ) : (
          <Typography color="text.secondary">{t('pages:contacts.no')}</Typography>
        )}
      </TableCell>
    </TableRow>
  );
}
export default function ContactDetailPage() {
  const { t } = useTranslation(['pages', 'common']);
  const { campaignId, contactId } = useParams<{ campaignId: string; contactId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    page: callPage,
    rowsPerPage: callRowsPerPage,
    setPage: setCallPage,
    setRowsPerPage: setCallRowsPerPage,
    getPageLink: getCallPageLink,
  } = useListSearchParams({
    pageParam: 'call_page',
    pageSizeParam: 'call_page_size',
    defaultPage: 1,
    defaultPageSize: 10,
    defaultOrdering: '-created_at',
  });
  
  // Cost visibility permissions
  const { hasPermission } = usePermissions();
  const { isSuperuser } = useAuth();
  const { activeCallId: selectedCallId, openCallDetail } = useCallDetailDrawer();
  const canViewCostCredits = hasPermission('view_metrics');
  const canViewCostCurrency = hasPermission('view_metrics');
  const [campaignType, setCampaignType] = useState<CampaignType>('ai');
  const isReverseIvrCampaign = campaignType === 'reverse_ivr';
  const showTotalCredits = canViewCostCredits && !isReverseIvrCampaign;
  const showCallHistoryOriginAndAgentColumns = !isReverseIvrCampaign;
  const showCallHistoryConsumptionColumn = !isReverseIvrCampaign;
  const showCallHistoryLatencyAndCostColumns = canViewCostCurrency && !isReverseIvrCampaign;
  
  // Number formatting
  const { formatNumber, formatCurrency } = useNumberFormat();
  
  const [contact, setContact] = useState<CampaignContactDetail | null>(null);
  const rawContactVariables = (contact?.variables ?? {}) as Record<string, unknown>;
  const filteredContactVariableEntries = Object.entries(rawContactVariables).filter(([key]) => {
    if (isSuperuser) return true;
    const normalizedKey = key.toLowerCase();
    return !(normalizedKey.startsWith('sys__') || normalizedKey.startsWith('system__'));
  });
  const contactDescriptor = contact?.displayName || contact?.phone;
  usePageTitle(t('pages:contacts.title'), contactDescriptor);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [markingConverted, setMarkingConverted] = useState(false);
  const [markingOptout, setMarkingOptout] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [optoutDialogOpen, setOptoutDialogOpen] = useState(false);
  const [preCallEnabled, setPreCallEnabled] = useState(false);
  const [preCallHistory, setPreCallHistory] = useState<PreCallExecutionHistoryItem[]>([]);
  const [preCallHistoryLoading, setPreCallHistoryLoading] = useState(false);
  const [preCallAction, setPreCallAction] = useState<'retry' | 'reset' | null>(null);
  const [preCallReason, setPreCallReason] = useState('');
  const [preCallActionLoading, setPreCallActionLoading] = useState(false);
  
  const callRowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});
  const handleCallSelection = (call: CallHistoryItem) => {
    const fallbackContactVariables = Object.entries(contact?.variables ?? {}).reduce<Record<string, string>>(
      (accumulator, [key, value]) => {
        if (value === null || value === undefined) {
          return accumulator;
        }
        accumulator[key] = String(value);
        return accumulator;
      },
      {},
    );
    openCallDetail(call.id, { contactVariables: fallbackContactVariables });
  };

  // WebSocket for real-time updates
  const { isConnected: wsConnected, activeCall } = useContactWebSocket(contactId, {
    onStatusChange: useCallback((event: ContactObserverEvent) => {
      setContact((prev) => (prev ? { ...prev, ...event } : prev));
    }, []),
  });

  const fetchContact = useCallback(async () => {
    try {
      const res = await api.get(`/api/campaign-contacts/${contactId}/`);
      setContact(res.data);
      setError(null);
    } catch (err) {
      console.error('Error loading contact:', err);
      setError(t('pages:contacts.errors.loading'));
    } finally {
      setLoading(false);
    }
  }, [contactId, t]);

  useEffect(() => {
    fetchContact();
  }, [fetchContact]);

  const fetchCampaignType = useCallback(async () => {
    if (!campaignId) return;
    try {
      const res = await api.get(`/api/campaigns/${campaignId}/`);
      const nextCampaignType = res.data?.campaignType === 'reverse_ivr' ? 'reverse_ivr' : 'ai';
      setCampaignType(nextCampaignType);
    } catch (err) {
      console.error('Error loading campaign type:', err);
      setCampaignType('ai');
    }
  }, [campaignId]);

  useEffect(() => {
    fetchCampaignType();
  }, [fetchCampaignType]);

  const fetchPreCallData = useCallback(async () => {
    if (!campaignId || !contactId) return;
    setPreCallHistoryLoading(true);
    try {
      const [statusResponse, historyResponse] = await Promise.all([
        api.get(`/api/campaigns/${campaignId}/pre-call-status/`),
        api.get(`/api/campaigns/${campaignId}/contacts/${contactId}/pre-call-executions/`),
      ]);
      setPreCallEnabled(Boolean(statusResponse.data?.enabled));
      setPreCallHistory(historyResponse.data?.results || []);
    } catch (fetchError) {
      console.error('Error loading pre-call history:', fetchError);
      setPreCallEnabled(false);
      setPreCallHistory([]);
    } finally {
      setPreCallHistoryLoading(false);
    }
  }, [campaignId, contactId]);

  useEffect(() => {
    void fetchPreCallData();
  }, [fetchPreCallData]);

  const canOperatePreCall = hasPermission('campaigns_update') && hasPermission('scripts_execute');

  const executePreCallAction = async () => {
    if (!campaignId || !contactId || !preCallAction || !preCallReason.trim()) return;
    setPreCallActionLoading(true);
    try {
      await api.post(
        `/api/campaigns/${campaignId}/contacts/${contactId}/pre-call-${preCallAction}/`,
        { reason: preCallReason.trim() },
        { headers: { 'Idempotency-Key': crypto.randomUUID() } },
      );
      setPreCallAction(null);
      setPreCallReason('');
      await Promise.all([fetchContact(), fetchPreCallData()]);
    } catch (actionError) {
      console.error('Error operating pre-call:', actionError);
      setError(t('pages:contacts.preCall.actionFailed'));
    } finally {
      setPreCallActionLoading(false);
    }
  };

  useContactCallRealtime({
    contactId,
    wsConnected,
    onCallChanged: fetchContact,
  });
  
  // Handle call parameter from URL - expand and scroll to specific call
  useEffect(() => {
    if (selectedCallId && contact?.callHistory) {
      // Find the call in history (id is the UUID from backend)
      const call = contact.callHistory.find((c) => c.id === selectedCallId);
      if (call) {
        const callIndex = contact.callHistory.findIndex((c) => c.id === selectedCallId);
        if (callIndex >= 0) {
          const targetPage = Math.floor(callIndex / callRowsPerPage);
          if (targetPage !== callPage) {
            setCallPage(targetPage);
          }
        }

        // Scroll to the call row after delays to ensure DOM is ready
        setTimeout(() => {
          const rowElement = callRowRefs.current[call.id];
          if (rowElement) {
            // First scroll to get it in view
            rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

            // Wait for expansion animation, then scroll again for better positioning
            setTimeout(() => {
              rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 500);
          } else {
            // If element not found, try again after a longer delay
            setTimeout(() => {
              const retryElement = callRowRefs.current[call.id];
              if (retryElement) {
                retryElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }, 1000);
          }
        }, 100);
      }
    }
  }, [callPage, callRowsPerPage, contact, selectedCallId, setCallPage]);

  const handleMarkConverted = async () => {
    if (!contact) return;
    
    setMarkingConverted(true);
    try {
      await api.post(`/api/campaign-contacts/${contactId}/mark_converted/`);
      fetchContact();
      setConfirmDialogOpen(false);
    } catch (err) {
      console.error('Error marking converted:', err);
      setError(t('pages:contacts.errors.markingConverted'));
    } finally {
      setMarkingConverted(false);
    }
  };

  const handleMarkOptout = async () => {
    if (!contact) return;
    
    setMarkingOptout(true);
    try {
      await api.post(`/api/campaign-contacts/${contactId}/mark_optout/`);
      fetchContact();
      setOptoutDialogOpen(false);
    } catch (err) {
      console.error('Error marking optout:', err);
      setError('Failed to mark contact as opt-out');
    } finally {
      setMarkingOptout(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !contact) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || t('common:common.noData')}</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          {t('common:actions.back')}
        </Button>
      </Box>
    );
  }

  const callHistory = contact.callHistory ?? [];
  const paginatedCallHistory = callHistory.slice(
    callPage * callRowsPerPage,
    callPage * callRowsPerPage + callRowsPerPage,
  );
  const callHistoryCount = callHistory.length;

  const handleDetailBack = () => {
    const returnTab = searchParams.get('returnTab') || 'contacts';
    navigate(`/campaigns/${campaignId}${returnTab === 'calls' ? '?tab=calls' : ''}`);
  };

  const detailBreadcrumbs = [
    { label: contact.campaignName, to: `/campaigns/${campaignId}` },
    { label: t('pages:contacts.title') },
  ];

  const headerActions = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      {activeCall && (
            <Chip
              icon={<PhoneInTalkIcon />}
          label={t('pages:contacts.callInProgress', 'Chamada em andamento')}
          color="success"
          size="small"
          sx={{
            animation: 'pulse 1.5s infinite',
            '@keyframes pulse': {
              '0%': { opacity: 1 },
              '50%': { opacity: 0.6 },
              '100%': { opacity: 1 },
            },
          }}
        />
      )}
      {wsConnected && (
        <Tooltip title={t('pages:contacts.realtimeUpdates', 'Atualizacoes em tempo real ativas')}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: 'success.main',
            }}
          />
        </Tooltip>
      )}
      {contact.isOptout && (
        <Chip
          icon={<DoNotDisturb />}
          label={t('pages:contacts.isOptout')}
          size="medium"
          sx={{ bgcolor: '#9c27b0', color: 'white' }}
        />
      )}
      {contact.isConverted && (
        <Chip
          icon={<Verified />}
          label={t('pages:contacts.isConverted')}
          color="success"
          size="medium"
        />
      )}
      {!contact.isConverted && !contact.isOptout && (
        <>
          <Button
            variant="outlined"
            startIcon={<DoNotDisturb />}
            onClick={() => setOptoutDialogOpen(true)}
            sx={{
              borderColor: '#9c27b0',
              color: '#9c27b0',
              '&:hover': { borderColor: '#7b1fa2', bgcolor: 'rgba(156, 39, 176, 0.04)' },
            }}
          >
            {t('pages:contacts.markOptout', 'Mark Opt-out')}
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<Verified />}
            onClick={() => setConfirmDialogOpen(true)}
          >
            {t('pages:contacts.markConverted', 'Mark as Converted')}
          </Button>
        </>
      )}
    </Box>
  );

  return (
    <Box sx={{ pb: 4 }}>
      <DetailHeader
        title={formatDisplayName(contact.displayName)}
        sectionIcon={<ContactPageIcon color="primary" />}
        entityId={contact.id}
        breadcrumbs={detailBreadcrumbs}
        actions={headerActions}
        onBack={handleDetailBack}
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
        {/* Contact Info */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Badge color="primary" /> {t('pages:contacts.contactInfo')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              {filteredContactVariableEntries.map(([key, value]) => (
                <Box key={key}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                    {key}
                  </Typography>
                  <Typography variant="body1">{value === null || value === undefined || value === '' ? '-' : String(value)}</Typography>
                </Box>
              ))}
            </Box>
            
            {filteredContactVariableEntries.length === 0 && (
              <Typography color="text.secondary">{t('pages:contacts.noAdditionalData')}</Typography>
            )}
          </CardContent>
        </Card>

        {/* Phones */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhoneIphone color="primary" /> {t('pages:contacts.phones')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('pages:contacts.table.phone')}</TableCell>
                    <TableCell>{t('pages:contacts.table.label')}</TableCell>
                    <TableCell>{t('pages:contacts.table.status')}</TableCell>
                    <TableCell align="center">{t('pages:contacts.table.calls')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contact.phones.map((phone, idx) => (
                    <TableRow key={phone.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Phone fontSize="small" color="action" />
                          <Typography sx={{ fontFamily: 'monospace' }}>{formatPhone(phone.phone)}</Typography>
                          {idx === 0 && (
                            <Chip label={t('pages:contacts.table.primary')} size="small" color="primary" variant="outlined" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{phone.label || '-'}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {phoneStatusIcons[phone.status]}
                          <Typography variant="body2">{t(`pages:contacts.phoneStatus.${phone.status}` as never)}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">{phone.callsCount ?? 0}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {callHistoryCount > callRowsPerPage && (
              <Box sx={{ mt: 2 }}>
                <LinkPagination
                  component="div"
                  count={callHistoryCount}
                  page={callPage}
                  onPageChange={(_, newPage) => setCallPage(newPage)}
                  rowsPerPage={callRowsPerPage}
                  onRowsPerPageChange={(event) => setCallRowsPerPage(Number(event.target.value))}
                  rowsPerPageOptions={[10, 25, 50]}
                  labelRowsPerPage={t('common:pagination.rowsPerPage')}
                  labelDisplayedRows={({ from, to, count }) =>
                    count !== -1
                      ? `${from}-${to} ${t('common:common.of')} ${count}`
                      : `${from}-${to} ${t('common:common.of')} ${t('common:common.total')} >${to}`
                  }
                  getItemAriaLabel={(type) => {
                    if (type === 'first') return t('common:pagination.firstPage');
                    if (type === 'last') return t('common:pagination.lastPage');
                    if (type === 'next') return t('common:pagination.nextPage');
                    return t('common:pagination.previousPage');
                  }}
                  getPageLink={getCallPageLink}
                />
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Stats Summary */}
      <Card sx={{ my: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BarChart color="primary" /> {t('pages:contacts.summaryTitle')}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 3 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700}>{contact.totalCalls}</Typography>
              <Typography variant="caption" color="text.secondary">{t('pages:contacts.summary.totalCalls')}</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} color="success.main">{contact.connectedCalls}</Typography>
              <Typography variant="caption" color="text.secondary">{t('pages:contacts.summary.completed')}</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} color="error.main">{contact.failedCalls}</Typography>
              <Typography variant="caption" color="text.secondary">{t('pages:contacts.summary.failed')}</Typography>
            </Box>
            {!isReverseIvrCampaign && (
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>
                  <ContactConsumptionDisplay
                    aiBilledSeconds={contact.aiBilledSeconds ?? 0}
                    aiGrossBilledSeconds={contact.aiGrossBilledSeconds ?? contact.aiBilledSeconds ?? 0}
                    aiRefundedSeconds={contact.aiRefundedSeconds ?? 0}
                    telephonyBilledSeconds={contact.telephonyBilledSeconds ?? 0}
                    telephonyGrossBilledSeconds={contact.telephonyGrossBilledSeconds ?? contact.telephonyBilledSeconds ?? 0}
                    telephonyRefundedSeconds={contact.telephonyRefundedSeconds ?? 0}
                    aiDurationSeconds={contact.aiDurationSeconds ?? 0}
                    telephonyDurationSeconds={contact.telephonyDurationSeconds ?? 0}
                    billedSeconds={contact.totalBilledSeconds ?? contact.totalDuration}
                    effectiveSeconds={contact.totalDuration}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {t('pages:contacts.summary.totalBilledDuration')}
                </Typography>
              </Box>
            )}
            {canViewCostCurrency && !isReverseIvrCampaign && (
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" fontWeight={700}>{formatCurrency(contact.totalCost ?? 0, 'USD')}</Typography>
                <Typography variant="caption" color="text.secondary">{t('pages:contacts.summary.totalCost')}</Typography>
              </Box>
            )}
            {showTotalCredits && (
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" fontWeight={700}>{formatNumber(contact.totalCostCredits ?? 0)}</Typography>
                <Typography variant="caption" color="text.secondary">{t('pages:contacts.summary.totalCredits')}</Typography>
              </Box>
            )}
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700}>{contact.attemptCount}</Typography>
              <Typography variant="caption" color="text.secondary">{t('pages:contacts.summary.attempts')}</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {preCallEnabled && (
        <Box sx={{ mt: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="h6">{t('pages:contacts.preCall.title')}</Typography>
              <Tooltip title={t('pages:contacts.preCall.help')} arrow>
                <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
            </Box>
            {canOperatePreCall && (
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title={t('pages:contacts.preCall.retry')} arrow>
                  <IconButton size="small" onClick={() => setPreCallAction('retry')}>
                    <Refresh fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('pages:contacts.preCall.reset')} arrow>
                  <IconButton size="small" onClick={() => setPreCallAction('reset')}>
                    <RestartAlt fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
          {preCallHistoryLoading ? (
            <CircularProgress size={20} />
          ) : preCallHistory.length === 0 ? (
            <Typography variant="body2" color="text.secondary">{t('pages:contacts.preCall.empty')}</Typography>
          ) : (
            <TableContainer component={Card} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('pages:contacts.preCall.table.when')}</TableCell>
                    <TableCell>{t('pages:contacts.preCall.table.decision')}</TableCell>
                    <TableCell>{t('pages:contacts.preCall.table.reason')}</TableCell>
                    <TableCell>{t('pages:contacts.preCall.table.next')}</TableCell>
                    <TableCell align="right">{t('pages:contacts.preCall.table.duration')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preCallHistory.map((execution) => (
                    <TableRow key={execution.executionId}>
                      <TableCell>{formatDateTime(execution.createdAt)}</TableCell>
                      <TableCell>
                        <Chip
                          label={t(`pages:contacts.preCall.decisions.${execution.decision}`)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {execution.reasonCode
                          ? t(`pages:contacts.preCall.reasonCodes.${execution.reasonCode}`, {
                            defaultValue: execution.reasonCode,
                          })
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {formatDateTime(execution.nextRetryAt || execution.validUntil || '', { fallback: '-' })}
                      </TableCell>
                      <TableCell align="right">{execution.executionTimeMs ?? '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* Call History */}
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PhoneInTalkIcon color="primary" /> {t('pages:contacts.callHistory')}
      </Typography>
      
      {callHistoryCount > 0 ? (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('pages:contacts.table.date')}</TableCell>
                    <TableCell>{t('pages:contacts.table.phone')}</TableCell>
                    {showCallHistoryOriginAndAgentColumns && <TableCell>{t('pages:contacts.table.agent')}</TableCell>}
                    {showCallHistoryOriginAndAgentColumns && <TableCell>{t('pages:contacts.table.from')}</TableCell>}
                    <TableCell>{t('pages:contacts.table.status')}</TableCell>
                    {showCallHistoryConsumptionColumn && <TableCell>{t('pages:calls.consumption')}</TableCell>}
                    {showCallHistoryLatencyAndCostColumns && <TableCell>{t('pages:contacts.table.latency')}</TableCell>}
                    {showCallHistoryLatencyAndCostColumns && <TableCell align="right">{t('pages:contacts.cost')}</TableCell>}
                    <TableCell>{t('pages:contacts.table.converted')}</TableCell>
                    <TableCell>{t('pages:contacts.table.optout')}</TableCell>
                    <TableCell>{t('pages:contacts.table.mismatch')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedCallHistory.map((call) => {
                    // Create callback ref for this call
                    const setRowRef = (element: HTMLTableRowElement | null) => {
                      if (element) {
                        callRowRefs.current[call.id] = element;
                      } else {
                        delete callRowRefs.current[call.id];
                      }
                    };
                    
                    return (
                      <CallDetailRow 
                        key={call.id} 
                        call={call} 
                        canViewCostCurrency={canViewCostCurrency}
                        isReverseIvrCampaign={isReverseIvrCampaign}
                        isSelected={selectedCallId === call.id}
                        onSelect={handleCallSelection}
                        rowRef={setRowRef}
                      />
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent>
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No calls have been made to this contact yet.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Mark Converted Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Verified sx={{ fontSize: 20, color: 'success.main' }} />
            {t('pages:contacts.dialogs.markConverted.title')}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t('pages:contacts.dialogs.markConverted.message', { displayName: formatDisplayName(contact.displayName) })}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('pages:contacts.dialogs.markConverted.description')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>{t('common:actions.cancel')}</Button>
          <Button 
            variant="contained" 
            color="success" 
            onClick={handleMarkConverted}
            disabled={markingConverted}
          >
            {markingConverted ? t('pages:contacts.dialogs.markConverted.marking') : t('pages:contacts.dialogs.markConverted.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mark Optout Dialog */}
      <Dialog open={optoutDialogOpen} onClose={() => setOptoutDialogOpen(false)}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DoNotDisturb sx={{ fontSize: 20, color: 'warning.main' }} />
            {t('pages:contacts.dialogs.markOptout.title')}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t('pages:contacts.dialogs.markOptout.message', { displayName: formatDisplayName(contact.displayName) })}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('pages:contacts.dialogs.markOptout.description')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOptoutDialogOpen(false)}>{t('common:actions.cancel')}</Button>
          <Button 
            variant="contained" 
            onClick={handleMarkOptout}
            disabled={markingOptout}
            sx={{ bgcolor: '#9c27b0', '&:hover': { bgcolor: '#7b1fa2' } }}
          >
            {markingOptout ? t('pages:contacts.dialogs.markOptout.marking') : t('pages:contacts.dialogs.markOptout.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={preCallAction !== null} onClose={() => setPreCallAction(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {preCallAction === 'retry' ? t('pages:contacts.preCall.retry') : t('pages:contacts.preCall.reset')}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            required
            multiline
            minRows={3}
            label={t('pages:contacts.preCall.reason')}
            value={preCallReason}
            onChange={(event) => setPreCallReason(event.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreCallAction(null)}>{t('common:actions.cancel')}</Button>
          <Button
            variant="contained"
            onClick={() => void executePreCallAction()}
            disabled={!preCallReason.trim() || preCallActionLoading}
          >
            {preCallAction === 'retry' ? t('pages:contacts.preCall.retry') : t('pages:contacts.preCall.reset')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
